import {
  Component,
  OnInit,
  input,
  effect,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CalendarService,
  CalendarItem,
  CalendarEvent,
  User,
} from '../../../core/services/calendar.service';
import { Observable } from 'rxjs';
import { MultiSelectModule } from 'primeng/multiselect';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

interface CalendarEventForm {
  title: string;
  description: string;
  color: string;
  visible: boolean;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  employees: User[];
  address: string;
  link: string;
}

@Component({
  selector: 'app-calendar-event-form',
  standalone: true,
  imports: [CommonModule, FormsModule, MultiSelectModule, TranslocoPipe],
  templateUrl: './calendar-event-form.component.html',
  styleUrls: ['./calendar-event-form.component.scss'],
})
export class CalendarEventFormComponent implements OnInit {
  initialStartDate = input<string>();
  initialEndDate = input<string>();
  initialStartTime = input<string>();
  initialEndTime = input<string>();
  editingEvent = input<CalendarEvent>();
  selectedCalendarId = input<string>();
  eventSubmitted = output<{ event: CalendarEvent; calendarId: string }>();

  event: CalendarEventForm = {
    title: '',
    description: '',
    color: '#3b82f6',
    visible: true,
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    employees: [],
    address: '',
    link: '',
  };

  calendars$: Observable<CalendarItem[]>;
  calendars: CalendarItem[] = [];

  // Employee selection
  selectedEmployees: User[] = [];
  availableUsers: User[] = [];
  
  // Internal selectedCalendarId for two-way binding
  _selectedCalendarId?: string;

  colorOptions = [
    { label: 'Soft Red', value: '#fcafaf' },
    { label: 'Soft Pink', value: '#f8c8dc' },
    { label: 'Peach', value: '#ffd8b1' },
    { label: 'Light Orange', value: '#ffe5a8' },
    { label: 'Soft Yellow', value: '#fff4b2' },
    { label: 'Pale Lime', value: '#e0f7c2' },
    { label: 'Mint Green', value: '#baf2c2' },
    { label: 'Light Teal', value: '#a8f0e6' },
    { label: 'Sky Blue', value: '#a8dfff' },
    { label: 'Baby Blue', value: '#b3ccff' },
    { label: 'Lavender', value: '#d9c7f2' },
    { label: 'Soft Purple', value: '#e5c8f8' },
  ];

  private calendarService = inject(CalendarService);
  private translocoService = inject(TranslocoService);

  constructor() {
    this.calendars$ = this.calendarService.calendars$;
    
    // Effect to handle initial date/time changes
    effect(() => {
      const startDate = this.initialStartDate();
      const endDate = this.initialEndDate();
      const startTime = this.initialStartTime();
      const endTime = this.initialEndTime();
      
      if (startDate) {
        this.event.start_date = startDate;
      }
      if (endDate) {
        this.event.end_date = endDate;
      }
      if (startTime) {
        this.event.start_time = startTime;
      }
      if (endTime) {
        this.event.end_time = endTime;
      }
    });
    
    // Effect to handle editing event changes
    effect(() => {
      const event = this.editingEvent();
      if (event) {
        this.populateFormForEdit();
      } else {
        this.resetForm();
      }
    });
    
    // Effect to handle selected calendar ID changes
    effect(() => {
      const calendarId = this.selectedCalendarId();
      if (calendarId) {
        this._selectedCalendarId = calendarId;
      }
    });
  }

  ngOnInit() {
    this.calendars$.subscribe((calendars) => {
      this.calendars = calendars;
      const currentCalendarId = this.selectedCalendarId() || this._selectedCalendarId;
      if (calendars.length > 0 && !currentCalendarId) {
        this._selectedCalendarId = calendars[0].id;
      }
    });

    // Load available users
    this.availableUsers = this.calendarService.getFakeUsers();

    // Initialize form with initial values
    const startDate = this.initialStartDate();
    const endDate = this.initialEndDate();
    const startTime = this.initialStartTime();
    const endTime = this.initialEndTime();
    
    if (startDate) {
      this.event.start_date = startDate;
    }
    if (endDate) {
      this.event.end_date = endDate;
    }
    if (startTime) {
      this.event.start_time = startTime;
    }
    if (endTime) {
      this.event.end_time = endTime;
    }

    // If editingEvent is already available, populate the form
    if (this.editingEvent()) {
      this.populateFormForEdit();
    }
  }


  private populateFormForEdit(): void {
    const event = this.editingEvent();
    if (!event) return;
    // Populate form with existing event data
    this.event.title = event.title || '';
    this.event.description = event.description || '';
    this.event.color = event.color || '#3b82f6';
    this.event.address = event.address || '';
    this.event.link = event.link || '';

    // Parse start and end dates/times
    if (event.start) {
      const startDate = new Date(event.start);
      this.event.start_date = startDate.toISOString().split('T')[0];
      this.event.start_time = startDate.toTimeString().split(' ')[0].substring(0, 5);
    }

    if (event.end) {
      const endDate = new Date(event.end);
      this.event.end_date = endDate.toISOString().split('T')[0];
      this.event.end_time = endDate.toTimeString().split(' ')[0].substring(0, 5);
    }

    // Set selected employees
    this.selectedEmployees = event.employees || [];
  }

  private resetForm(): void {
    this.event = {
      title: '',
      description: '',
      color: '#3b82f6',
      visible: true,
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      employees: [],
      address: '',
      link: '',
    };
    this.selectedEmployees = [];
  }

  handleSubmit(): void {
    // Validate required fields
    if (!this.event.title?.trim()) {
      alert(this.translocoService.translate('calendar.eventForm.validation.titleRequired'));
      return;
    }

    if (!this.event.start_date) {
      alert(this.translocoService.translate('calendar.eventForm.validation.startDateRequired'));
      return;
    }

    if (!this.event.start_time) {
      this.event.start_time = '00:00';
    }

    if (!this.event.end_date) {
      this.event.end_date = this.event.start_date;
    }

    if (!this.event.end_time) {
      this.event.end_time = '23:59';
    }

    // Build start datetime string
    let startDateTime = this.event.start_date;
    startDateTime = `${this.event.start_date}T${this.event.start_time}:00`;

    // Build end datetime string
    let endDateTime = this.event.end_date;
    endDateTime = `${this.event.end_date}T${this.event.end_time}:00`;

    // Validate start and end times if both have times
    if (this.event.start_time && this.event.end_time) {
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);

      if (startDate >= endDate) {
        alert(this.translocoService.translate('calendar.eventForm.validation.timeValidation'));
        return;
      }
    }

    const eventData: CalendarEvent = {
      title: this.event.title,
      description: this.event.description,
      color: this.event.color,
      visible: this.event.visible,
      start: startDateTime,
      end: endDateTime,
      address: this.event.address,
      link: this.event.link,
      employees: this.selectedEmployees,
    };

    const calendarId = this.selectedCalendarId() || this._selectedCalendarId;
    this.eventSubmitted.emit({ event: eventData, calendarId: calendarId || '' });
  }

  selectCalendar(calendarId: string): void {
    this._selectedCalendarId = calendarId;
  }

  selectColor(color: string): void {
    this.event.color = color;
  }

  toggleVisibility(): void {
    this.event.visible = !this.event.visible;
  }
}
